<template>
    <div>
        <h6
            style="width: 100%; text-align: center; margin-bottom: 20px"
            v-if="payload.helperText"
        >
            {{ payload.helperText }}
        </h6>
        <transition name="slide" mode="out-in">
            <div key="1" v-if="!emailHint && !loading">
                <form @submit.prevent="handleClick">
                    <h6 class="subtitle bold">
                        {{
                            $t(
                                'components.miscellaneous.verifyModuleDialog.university'
                            )
                        }}
                    </h6>
                    <BaseSearchable
                        v-model="school"
                        :data="schoolData()"
                        class="mb-4"
                        required
                    />
                    <h6 class="subtitle bold">
                        {{
                            $t(
                                'components.miscellaneous.verifyModuleDialog.studentEmail'
                            )
                        }}
                    </h6>
                    <div
                        class="d-flex flex-row align-items-center custom-outline"
                        style="border: 1px solid rgba(0,0,0,0.4); border-radius: var(--border-radius); padding: .5px .5px;"
                    >
                        <TextField
                            required
                            v-model="email"
                            :placeholder="
                                $t(
                                    'components.miscellaneous.verifyModuleDialog.studentEmailPlaceholder'
                                )
                            "
                            style="flex-basis: 0; flex-grow: 1; border: none;"
                            class="remove-background-from-children rightAlignTextInChildren"
                            :outline="false"
                        />
                        <p class="m-0 noselect">@</p>
                        <BaseSearchable
                            required
                            v-model="emailDomain"
                            :data="uniDomains"
                            style="flex-basis: 0; flex-grow: 1;"
                            id="emailDomainSelector"
                            :outline="false"
                        />

                        <b-tooltip
                            :show.sync="showToolTip"
                            ref="emailDomainToolTip"
                            :disabled="true"
                            target="emailDomainSelector"
                            title="Please choose the right email domain here"
                        />
                    </div>

                    <BaseButton
                        submit
                        style="display: block; margin: 20px auto; width: 100%; max-width: 300px;"
                        class="outline-primary mt-5"
                    >
                        {{
                            $t(
                                'components.miscellaneous.verifyModuleDialog.verify'
                            )
                        }}
                    </BaseButton>
                </form>
            </div>
            <div key="2" v-else-if="loading">
                <Spinner
                    class="primary-color"
                    style="display: block; margin: 40px auto;"
                />
            </div>
            <div key="3" v-else>
                <h6
                    class="bold px-4"
                    v-html="
                        $t(
                            'components.miscellaneous.verifyModuleDialog.emailHasBeenSentTo',
                            {
                                email: fullEmailAddress,
                                schoolName: school.label,
                            }
                        )
                    "
                />

                <div class="py-2 px-4 mt-5">
                    <h6 class="mute text-center caption">
                        {{
                            $t(
                                'components.miscellaneous.verifyModuleDialog.ifNoEmailRecieved'
                            )
                        }}
                    </h6>
                    <BaseButton
                        class="my-1"
                        style="display: block; margin: 8px auto;"
                        @click="contactHelp"
                        tiny
                    >
                        {{
                            $t(
                                'components.miscellaneous.verifyModuleDialog.contactHelp'
                            )
                        }}
                    </BaseButton>
                </div>

                <BaseButton
                    @click="closeDialog"
                    style="display: block; margin: 20px auto; width: 100%; max-width: 300px;"
                    class="outline-primary mt-1"
                >
                    {{ $t('general.ok') }}
                </BaseButton>
            </div>
        </transition>
    </div>
</template>

<script>
// Components
import Spinner from '~/components/builders/Spinner';

// Utils
import { getSubDomain, emailVerificationURL } from '~/utils/urlUtils';
import { uniConnectionErrorMapper } from '~/utils/errors/uniErrorMapper';
import DialogService from '~/services/DialogService';
import IntercomService from '~/services/IntercomService';
import UniversityService from '~/services/UniversityService';

import { extractErrorResponse } from '~/utils/errors/authErrorMapper';

import BackgroundProcessesService from '~/services/BackgroundProcessesService';

export default {
    name: 'VerifyModuleDialog',
    components: { Spinner },
    data() {
        return {
            school: undefined,
            emailHint: false,
            queryLoading: false,
            email: '',
            emailDomain: undefined,
            shouldForceUpdate: true,
            loadingConnections: true,

            showToolTip: false,
        };
    },
    computed: {
        schools() {
            return this.$interfaces.university.get({ reactive: true });
        },

        loading() {
            return (
                this.schools.loading ||
                this.queryLoading ||
                this.loadingConnections
            );
        },

        connections() {
            let forceUpdate = this.shouldForceUpdate;
            this.shouldForceUpdate = false;
            return this.$interfaces.university.connection.get({
                forceUpdate,
                reactive: true,
            });
        },

        connectionsLoading() {
            return this.connections.loading;
        },

        payload() {
            return this.$interfaces.dialogModal.getState().payload || {};
        },

        currentSchool() {
            if (!this.schools.loading) {
                const currentSubDomain = UniversityService.getCurrentDomain();
                let currentSchool = this.schools.data.find(
                    it => it.subDomain === currentSubDomain
                );

                if (!currentSchool && this.schools.data.length > 0) {
                    currentSchool = this.schools.data[0];
                } else if (!currentSchool) {
                    return {};
                }

                return { value: currentSchool.name, label: currentSchool.name };
            }
        },

        selectedSchool() {
            return (this.schools &&
            this.schools.data &&
            this.schools.data.length > 0
                ? this.schools.data
                : []
            ).find(it => it.name === ((this.school || {}).label || ''));
        },

        fullEmailAddress() {
            return this.email && this.emailDomain
                ? this.email.split('@')[0].concat('@', this.emailDomain.value)
                : null;
        },

        uniDomains() {
            if (this.school && this.selectedSchool) {
                const school = this.selectedSchool;
                const domains = school.domains.map(it => ({
                    value: it,
                    label: it,
                }));
                this.emailDomain =
                    domains && domains.length > 0 ? domains[0] : undefined;
                return domains;
            }
        },
    },
    methods: {
        schoolData() {
            return this.schools.data.map(s => ({ value: s.id, label: s.name }));
        },
        loadingSchools() {
            return this.schools.loading;
        },
        async handleClick() {
            this.queryLoading = true;

            const school = this.selectedSchool;

            this.$interfaces.university.connection
                .post({
                    body: {
                        schoolId: school.id,
                        studentEmail: this.fullEmailAddress,
                        returnUrl: emailVerificationURL(),
                    },
                })
                .then(res => {
                    DialogService.setTitle(
                        this.$t(
                            'components.miscellaneous.verifyModule.verificationPending'
                        )
                    );
                    this.emailHint = true;
                    this.queryLoading = false;
                    // Start ping loop to update verification progress
                    BackgroundProcessesService.startVerificationPing();
                })
                .catch(error => {
                    let errors = extractErrorResponse(error) || [];
                    const err = errors.length > 0 ? errors[0] : {};

                    if (err.code === 'ExistingConnection') {
                        this.$interfaces.university.connection
                            .get({ forceUpdate: true })
                            .then(data => {
                                //Check if has pending, and if so open avatar dialog
                                let connection = data.data.find(
                                    it => !it.isVerified
                                );
                                if (data.data.length > 0 && connection) {
                                    this.$interfaces.dialogModal
                                        .getState()
                                        .resolver.res({
                                            isPending: true,
                                            connection: connection,
                                        });
                                } else {
                                    this.$interfaces.dialogModal
                                        .getState()
                                        .resolver.res({ isPending: false });
                                }
                                //Else accept promise with enum, so jobdetail can continue apply click
                            })
                            .catch(woops => (err = woops));
                    } else {
                        this.$interfaces.dialogModal.openModal({
                            title: 'Error',
                            content: uniConnectionErrorMapper(error),
                            actions: [
                                {
                                    title: this.$t('general.ok'),
                                    variant: 'primary',
                                    action: ({ res }) => res(),
                                },
                            ],
                        });
                    }
                });
        },
        closeDialog() {
            this.$interfaces.dialogModal.getState().resolver.res();
        },
        contactHelp() {
            IntercomService.show();
        },
    },

    watch: {
        currentSchool(newSchool) {
            if (this.school === undefined) this.school = newSchool;
        },
        connectionsLoading(isLoading, wasLoading) {
            if (!isLoading && wasLoading && this.loadingConnections) {
                this.loadingConnections = false;
                if (
                    !this.connections.loading &&
                    !this.schools.loading &&
                    this.payload.checkConnections
                ) {
                    let connection = this.connections.data.find(
                        it => it.school.name === this.currentSchool.value
                    );
                    if (connection && connection.isVerified) {
                        this.$interfaces.dialogModal
                            .getState()
                            .resolver.res({ isVerified: true });
                    } else if (connection) {
                        this.$interfaces.dialogModal.getState().resolver.res();
                        this.$interfaces.uniBadgeModule.setOpenAndSelected({
                            isOpen: true,
                            selected: connection,
                        });
                    }
                }
            }
        },
        email(newEmail, oldEmail) {
            if (newEmail.includes('@') || oldEmail.includes('@')) {
                this.$refs.emailDomainToolTip.$emit('enable');
                this.showToolTip = true;
            }
        },
    },

    mounted() {
        if (this.schools && this.schools.data && this.schools.data.length > 0) {
            this.school = this.currentSchool;
        }
    },
};
</script>

<style>
.custom-outline:focus-within {
    border-color: var(--primary) !important;
}
.remove-background-from-children input {
    background-color: transparent !important;
}
.remove-background-from-children > div > input:focus {
    outline-color: transparent !important;
}
.rightAlignTextInChildren input {
    text-align: right;
}
</style>
